import React, { useEffect, useMemo, useState } from 'react';
import ActionButton from '../components/ActionButton';
import DishCard from '../components/DishCard';
import ComboBanner from '../components/ComboBanner';
import ServicesSection from '../components/ServicesSection';
import DataStatusBanner from '../components/DataStatusBanner';
import servicesFallback from '../data/services';
import useRemoteData from '../hooks/useRemoteData';
import { fetchAddOns, fetchCombos, fetchMenuItems } from '../lib/api';

const heroLines = [
  'Jollof, fried rice, beans, soups, and plantain served fresh from our Asaba kitchen.',
  'Order daily meals, plan catering, or arrange equipment rentals from one trusted team.',
  'Built for quick lunches, family packs, and events that need dependable food service.',
];

const Home = () => {
  const [isMobileView, setIsMobileView] = useState(() => window.innerWidth <= 720);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 720px)');
    const handleMediaChange = (event) => {
      setIsMobileView(event.matches);
    };

    setIsMobileView(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleMediaChange);

    return () => {
      mediaQuery.removeEventListener('change', handleMediaChange);
    };
  }, []);

  const { data: menu, loading: menuLoading, error: menuError } = useRemoteData(fetchMenuItems, [], { cacheKey: 'menu-items' });
  const { data: combos, loading: combosLoading, error: combosError } = useRemoteData(fetchCombos, [], { cacheKey: 'combos' });
  const { data: addOns, loading: addOnsLoading, error: addOnsError } = useRemoteData(fetchAddOns, [], { cacheKey: 'dish-add-ons' });
  const services = servicesFallback;

  const filteredMenu = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();

    return normalizedQuery
      ? menu.filter((item) => {
          const name = item.name?.toLowerCase() || '';
          const description = item.description?.toLowerCase() || '';
          const category = item.category?.toLowerCase() || '';
          const tags = Array.isArray(item.tags)
            ? item.tags.join(' ').toLowerCase()
            : '';

          return (
            name.includes(normalizedQuery) ||
            description.includes(normalizedQuery) ||
            category.includes(normalizedQuery) ||
            tags.includes(normalizedQuery)
          );
        })
      : menu;
  }, [menu, searchQuery]);

  const previewMenu = useMemo(() => {
    if (searchQuery.trim()) {
      return filteredMenu;
    }

    return filteredMenu.slice(0, isMobileView ? 10 : 8);
  }, [filteredMenu, isMobileView, searchQuery]);

  const previewCombos = combos.slice(0, 4);

  return (
    <>
      <section className="hero" id="hero">
        <div className="hero-content">
          <p className="eyebrow">Vemmac Kitchen - Asaba</p>
          <h1>Local delicacies, crafted with pride</h1>
          <div className="hero-lines">
            {heroLines.map((line) => (
              <p key={line}>{line}</p>
            ))}
          </div>
          <div className="hero-actions">
            <ActionButton to="/menu" variant="primary" size="xs">
              Browse menu
            </ActionButton>
            <ActionButton to="/about" variant="outline" size="xs">
              Learn about us
            </ActionButton>
          </div>
        </div>
        <div className="hero-card">
          <p className="hero-label">Specialty</p>
          <h3>Jollof rice platter</h3>
          <p>Signature tomato-base rice with grilled plantain and spicy sauce.</p>
          <p className="hero-price">From NGN 3,200</p>
          <div className="hero-stats">
            <div>
              <strong>Daily meals</strong>
              <span>Fresh prep from the shop</span>
            </div>
            <div>
              <strong>Event support</strong>
              <span>Catering and rentals available</span>
            </div>
          </div>
        </div>
      </section>

      <section className="hero-mobile-teaser">
        <p>Fresh, locally inspired meals ready for everyday orders and celebrations.</p>
        <ActionButton to="/about" variant="outline" size="xs">
          About us
        </ActionButton>
      </section>

      <section className="menu-preview-section" id="menu">
        <div className="menu-preview-header">
          <div>
            <p className="eyebrow">Featured menu</p>
            <h2>Popular dishes from the kitchen</h2>
            <p className="menu-preview-subtitle">Freshly prepared daily. Search, choose, and add what you need.</p>
            <div className="home-menu-search search-wrapper">
              <span className="search-icon" aria-hidden="true">🔎</span>
              <input
                type="search"
                className="menu-search-input"
                placeholder="Search dishes"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                aria-label="Search dishes on home"
              />
            </div>
          </div>
          <div className="menu-preview-cta-top">
            <ActionButton to="/menu" variant="primary" size="xs">
              Browse full menu
            </ActionButton>
          </div>
        </div>
        <DataStatusBanner
          loading={menuLoading || addOnsLoading}
          error={menuError || addOnsError}
          empty={!menuLoading && !menuError && menu.length === 0}
          emptyMessage="Menu items are unavailable right now. Please check back shortly."
        />
        <div className="menu-preview-card">
          {!menuLoading && !menuError && menu.length > 0 && (
            previewMenu.length > 0 ? (
              previewMenu.map((dish) => (
                <DishCard key={dish.id} dish={dish} addOnOptions={addOns} />
              ))
            ) : (
              <p className="menu-empty-state"><strong>No dish found.</strong> Try another keyword.</p>
            )
          )}
        </div>
        <div className="menu-preview-footer">
          <p>Looking for a complete meal? Browse curated combo packages.</p>
          <div className="menu-preview-actions">
            <ActionButton to="/combos" variant="outline" size="xs">
              View combos
            </ActionButton>
          </div>
        </div>
      </section>

      <DataStatusBanner
        loading={combosLoading}
        error={combosError}
        empty={!combosLoading && !combosError && combos.length === 0}
        emptyMessage="Combo packages are unavailable right now."
      />
      {!combosLoading && !combosError && combos.length > 0 && (
        <ComboBanner
          combos={previewCombos}
          sectionClassName="home-combos"
          ctaLabel="View combo packages"
          ctaLink="/combos"
        />
      )}
      <ServicesSection services={services} />
    </>
  );
};

export default Home;
